import Alpine from 'alpinejs'
import morph from '@alpinejs/morph'

import type { TFilterStore } from './stores/filterStore'

// Type for checkbox filter groups, which can optionally include parent filter information for nested filters
type TFilterGroupValue = {
    filterValue: string
    parentName?: string
    parentKey?: string
}

export const initFilters = (): void => {
    Alpine.data('filters', () => ({
        isOpen: false,
        parentElem: null as HTMLElement | null,
        controller: null as AbortController | null,
        lastError: '',
        store: {} as TFilterStore,
        interestRate: 0,
        defaultTerms: 0,
        defaultDownPaymentPercentage: 0,
        defaultFinalTermPercentage: 0,

        init() {
            Alpine.plugin(morph)
            this.store = Alpine.store('filterStore') as TFilterStore

            // Get entry ID and parent element from the page
            this.store.entryId = (this.$el as HTMLElement).dataset.entryId || ''
            this.store.vehicleListId = (this.$el as HTMLElement).dataset.vehicleListId || ''
            this.store.vehicleFilterListId = (this.$el as HTMLElement).dataset.vehicleFilterListId || ''
            this.interestRate = Number((this.$el as HTMLElement).dataset.interestRate) || 0
            this.defaultTerms = Number((this.$el as HTMLElement).dataset.defaultTerms) || 0
            this.defaultDownPaymentPercentage = Number((this.$el as HTMLElement).dataset.defaultDownPaymentPercentage) || 0
            this.defaultFinalTermPercentage = Number((this.$el as HTMLElement).dataset.defaultFinalTermPercentage) || 0
            this.parentElem = this.$el

            this.$watch('$store.filterStore.sort', () => {
                this.applyFilters(false)
            })

            // Load initial filters
            this.loadInitialFilters()

            this.parentElem?.addEventListener('range-slider-change', () => {
                this.applyFilters(false)
            })
        },

        /**
         * Fetches the filters and vehicles from the API endpoint for given queryString
         *
         * @param queryString containing the required filters
         * @returns true if fetch was successful, false if not
         */
        async fetchAndUpdateFilters(queryString: string) {
            try {
                this.store.startLoading()

                // cancel in-flight request
                if (this.controller) this.controller.abort()

                this.controller = new AbortController()

                const response = await fetch(
                    `https://api.datamotive.nl/api/v1/stock/vehicle-list/${this.store.vehicleListId}/vehicles?filterId=${this.store.vehicleFilterListId}&${queryString}&attributes[]=transmission&attributes[]=yearOfConstruction&attributes[]=odometerReading&attributes[]=fuel&attributes[]=addition`,
                    {
                        signal: this.controller.signal,
                        method: 'GET',
                        headers: {
                            'Accept-Language': 'nl',
                        },
                    }
                )

                if (!response.ok) {
                    this.lastError = `Server error ${response.status}`
                    return false
                }

                const data = await response.json()
                if (data) {
                    this.store.count = data.count ?? 0
                    this.store.pageCount = data.pages.total ?? 0
                    this.store.pageCurrent = data.pages.current ?? 0
                    this.store.filters = data.filters ?? []
                    this.store.vehicleList = data.items ?? []
                    this.lastError = ''
                    return true
                }

                this.lastError = data?.error || 'Unknown API error'
                return false
            } catch (err: any) {
                if (err.name === 'AbortError') {
                    return false
                }
                this.lastError = 'Network request failed'
                console.error(err)
                return false
            } finally {
                this.controller = null
                this.store.stopLoading()
            }
        },

        /**
         * Loads all the filters on initialization
         * Takes into account active filters available in the URL querystring
         */
        async loadInitialFilters() {
            this.store.filters = []
            this.store.vehicleList = []

            // Get query parameters from current URL
            const urlParams = new URLSearchParams(window.location.search)

            // Build query string from URL params, or use defaults if none exist
            let queryString = ''
            if (urlParams.toString()) {
                // Use existing URL parameters
                queryString = urlParams.toString()
            } else {
                // Use default parameters if URL has no query string
                const queryParts = [`page=${this.store.pageCurrent}`, `pageSize=${this.store.pageSize}`, `sort=${this.store.sort}`]
                queryString = queryParts.join('&')
            }

            const success = await this.fetchAndUpdateFilters(queryString)

            if (success) {
                // Check the checkboxes based on URL parameters
                this.$nextTick(() => {
                    urlParams.forEach((value, key) => {
                        // Look for checkboxes matching the filter name and value
                        // Format: fuel[Electric]=, fuel[Diesel]=
                        // Handle encoded values like brand[%C5%A0koda]= (Škoda)
                        const match = key.match(/^(\w+)\[(.+)\]$/)
                        if (match) {
                            const filterName = match[1]
                            const encodedFilterValue = match[2]
                            // Decode the filter value to match the actual checkbox value
                            const filterValue = decodeURIComponent(encodedFilterValue)
                            const checkbox = this.$el.querySelector(
                                `input[type="checkbox"][name="${filterName}"][value="${filterValue}"]`
                            ) as HTMLInputElement
                            if (checkbox) {
                                checkbox.checked = true
                            }
                        }

                        // Handle checkboxes for nested filters with parent information: parent[ParentKey][FilterName][FilterValue]=
                        const nestedMatch = key.match(/^(\w+)\[(\w+)\]\[(\w+)\]\[(.+)\]$/)
                        if (nestedMatch) {
                            const parentName = nestedMatch[1]
                            const parentKey = nestedMatch[2]
                            const filterName = nestedMatch[3]
                            const encodedFilterValue = nestedMatch[4]
                            const filterValue = decodeURIComponent(encodedFilterValue)
                            const checkbox = this.$el.querySelector(
                                `input[type="checkbox"][name="${filterName}"][value="${filterValue}"][data-parentname="${parentName}"][data-parentkey="${parentKey}"]`
                            ) as HTMLInputElement
                            if (checkbox) {
                                checkbox.checked = true

                                // Also check parent filter if exists
                                const parentCheckbox = this.$el.querySelector(
                                    `input[type="checkbox"][name="${parentName}"][value="${parentKey}"]`
                                ) as HTMLInputElement
                                if (parentCheckbox) {
                                    parentCheckbox.checked = true

                                    // Open the parent filter group if it's a nested filter
                                    const parentFilterGroup = parentCheckbox.closest('details') as HTMLDetailsElement
                                    if (parentFilterGroup && !parentFilterGroup.open) {
                                        parentFilterGroup.open = true
                                    }
                                }
                            }
                        }
                    })
                    // Set active filter count after checkboxes are checked
                    this.setActiveFilterCount()
                })
            }
        },

        adjustChildFilters(el: HTMLInputElement, parentName: string, parentKey: string) {
            if (el.checked) {
                const details = el.closest('details')

                if (details && !details.open) {
                    details.open = true
                }
            } else {
                // Find all checkboxes that have the clicked checkbox as parent and uncheck them
                const childCheckboxes = this.parentElem?.querySelectorAll(
                    `input[type="checkbox"][data-parentname="${parentName}"][data-parentkey="${parentKey}"]`
                ) as NodeListOf<HTMLInputElement>

                childCheckboxes?.forEach((checkbox) => {
                    checkbox.checked = false
                })
            }

            this.applyFilters(false)
        },

        /**
         * Applies the filters as set in the filter bar.
         * Also updates the URL querystring to allow page
         * reloads and sharing.
         */
        async applyFilters(forcePage = false) {
            const checkboxes = this.parentElem?.querySelectorAll('input[type="checkbox"]:checked')
            const rangeSliders = this.parentElem?.querySelectorAll('.vlp__filters-values--number-range')

            // Build query string manually to avoid encoding brackets
            const queryParts: string[] = []

            // Add entry ID (this identifies the page/entry making the request)
            queryParts.push(`entryId=${this.store.entryId}`)
            queryParts.push(`pageSize=${this.store.pageSize}`)
            queryParts.push(`sort=${this.store.sort}`)

            if (forcePage) {
                queryParts.push(`page=${this.store.pageCurrent}`)
            } else {
                // Reset to first page when filters change
                queryParts.push('page=1')
                this.store.pageCurrent = 1
            }

            checkboxes?.forEach((checkbox: Element) => {
                const input = checkbox as HTMLInputElement
                const parentName = input.dataset.parentname
                const parentKey = input.dataset.parentkey

                if (parentName && parentKey) {
                    // find parent element and check it if not already checked
                    const parentCheckbox = this.parentElem?.querySelector(
                        `input[type="checkbox"][name="${parentName}"][value="${parentKey}"]`
                    ) as HTMLInputElement

                    if (parentCheckbox) {
                        parentCheckbox.checked = true
                    }
                }
            })

            // Add checked filter values
            const filterGroups = {} as Record<string, TFilterGroupValue[]>
            checkboxes?.forEach((checkbox: Element) => {
                const input = checkbox as HTMLInputElement
                const filterName = input.name
                const filterValue = input.value

                if (!filterGroups[filterName]) {
                    filterGroups[filterName] = []
                }

                const parentName = input.dataset.parentname
                const parentKey = input.dataset.parentkey

                if (parentName && parentKey) {
                    filterGroups[filterName].push({
                        parentName,
                        parentKey,
                        filterValue,
                    } as TFilterGroupValue)
                } else {
                    filterGroups[filterName].push({ filterValue } as TFilterGroupValue)
                }
            })

            // Add filters as checkbox format: ?fuel[Electric]=&fuel[Diesel]=
            Object.entries(filterGroups).forEach(([filterName, values]) => {
                const filterDef = this.store.filters.find((f: any) => f.key === filterName)
                const isSelectRangeCheckboxMode =
                    (filterDef as any)?.type === 'select-range' &&
                    Array.isArray((filterDef as any)?.options) &&
                    (filterDef as any)?.options.length <= 3

                if (isSelectRangeCheckboxMode) {
                    // For select-range checkboxes, aggregate all checked values to find min/max
                    const numericValues = values
                        .filter((v) => !v.parentName && !v.parentKey)
                        .map((v) => Number(v.filterValue))
                        .filter((v) => Number.isFinite(v))

                    if (numericValues.length > 0) {
                        const minVal = Math.min(...numericValues)
                        const maxVal = Math.max(...numericValues)
                        queryParts.push(`${filterName}[min][${minVal}]=`)
                        queryParts.push(`${filterName}[max][${maxVal}]=`)
                    }
                } else {
                    // Process each value individually for other filter types
                    values.forEach((value) => {
                        const isNumberRange = (filterDef as any)?.type === 'number-range'

                        // Use encodeURIComponent only for the value, not the brackets and filterName
                        if (value.parentName && value.parentKey) {
                            queryParts.push(`${value.parentName}[${value.parentKey}][${filterName}][${encodeURIComponent(value.filterValue)}]=`)
                        } else if (isNumberRange) {
                            queryParts.push(`${filterName}[min][${encodeURIComponent(value.filterValue)}]=`)
                            queryParts.push(`${filterName}[max][${encodeURIComponent(value.filterValue)}]=`)
                        } else {
                            queryParts.push(`${filterName}[${encodeURIComponent(value.filterValue)}]=`)
                        }
                    })
                }
            })

            // Add range slider values: ?price_min=1000&price_max=5000
            rangeSliders?.forEach((sliderElem: Element) => {
                const slider = sliderElem as HTMLElement
                const filterKey = slider.dataset.filterKey
                const minInput = slider.querySelector('.vlp__filters-range-slider--min') as HTMLInputElement
                const maxInput = slider.querySelector('.vlp__filters-range-slider--max') as HTMLInputElement

                if (filterKey && minInput && maxInput) {
                    const currentMin = parseInt(minInput.value)
                    const currentMax = parseInt(maxInput.value)
                    const changed = slider.dataset.minChanged === 'true' || slider.dataset.maxChanged === 'true'

                    if (Number.isFinite(currentMin) && Number.isFinite(currentMax)) {
                        if (changed) {
                            queryParts.push(`${filterKey}[min][${minInput.value}]=`)
                            queryParts.push(`${filterKey}[max][${maxInput.value}]=`)
                        }
                    }
                }
            })

            const queryString = queryParts.join('&')

            // Update the current URL with the filter parameters
            window.history.replaceState(null, '', `?${queryString}`)

            await this.fetchAndUpdateFilters(queryString)

            this.setActiveFilterCount()
            this.updateVehicleCards()

            window.scrollTo({ top: 0, behavior: 'smooth' })
        },

        /**
         * Resets all filters to their default state.
         */
        resetFilters() {
            // Uncheck all checkboxes and then trigger a filter update
            const checkboxes = this.parentElem?.querySelectorAll('input[type="checkbox"]')
            checkboxes?.forEach((checkbox: Element) => {
                const input = checkbox as HTMLInputElement
                input.checked = false
            })

            this.$dispatch('reset-range-sliders')

            this.applyFilters(false)
        },

        /**
         * Updates the active filters count to match the current amount of
         * selected options
         */
        setActiveFilterCount() {
            const checkboxes = this.parentElem?.querySelectorAll('input[type="checkbox"]:checked')

            // Count checked checkboxes
            const activeCheckboxCount = checkboxes ? checkboxes.length : 0

            const rangeSliders = this.parentElem?.querySelectorAll('.vlp__filters-values--number-range')
            let activeRangeFilters = 0

            // Count active range sliders
            rangeSliders?.forEach((sliderElem: Element) => {
                const slider = sliderElem as HTMLElement
                const filterKey = slider.dataset.filterKey
                const minInput = slider.querySelector('.vlp__filters-range-slider--min') as HTMLInputElement
                const maxInput = slider.querySelector('.vlp__filters-range-slider--max') as HTMLInputElement

                if (filterKey && minInput && maxInput) {
                    const absoluteMin = minInput.dataset.minimum ? parseInt(minInput.dataset.minimum) : 0
                    const absoluteMax = maxInput.dataset.maximum ? parseInt(maxInput.dataset.maximum) : 0

                    if (parseInt(minInput.value) > absoluteMin || parseInt(maxInput.value) < absoluteMax) {
                        activeRangeFilters += 1
                    }
                }
            })

            this.store.activeFilters = activeCheckboxCount + activeRangeFilters
        },

        /**
         * Checks in the URL querystring if a filter group is active.
         * This allows for opening or closing filter groups before data is loaded
         *
         * @param filterKey
         * @returns true if the filter has any selected options
         */
        isFilterActive(filterKey: string): boolean {
            // Check if any checkbox for this filter is checked in the URL parameters
            const urlParams = new URLSearchParams(window.location.search)
            let hasActiveOption = false

            urlParams.forEach((value, key) => {
                // Match filter[option] pattern

                const match = key.match(/^(\w+)\[([\w\s-]+)\]$/)

                if (match && match[1] === filterKey) {
                    hasActiveOption = true
                }

                // In case of range sliders, check for min/max parameters
                if (key.startsWith(`${filterKey}[min][`) || key.startsWith(`${filterKey}[max][`)) {
                    hasActiveOption = true
                }
            })

            return hasActiveOption
        },

        /**
         * Tests if a filter group should be open.
         * A filter group should be open if it is one of the first three
         * groups, or if it is considered "active"
         *
         * @param filterIndex
         * @param filter
         * @returns true if filter group should be open
         */
        shouldFilterBeOpen(filterIndex: number, filter: any): boolean {
            // Open first 3 filters by default
            if (filterIndex < 2) return true

            // Also open if filter has active options (based on URL parameters)
            return this.isFilterActive(filter.key)
        },

        /**
         * Use Alpine Morph to update the vehicle cards container
         */
        updateVehicleCards() {
            const cardsContainer = this.$refs.cardsContainer as HTMLElement

            if (cardsContainer) {
                // Based on filterStore.vehicleList, build new inner HTML
                let newCardsHTML = `<div x-ref="cardsContainer" class="vlp__cards">`

                const formatPurchasePrice = (value: number | string): string => {
                    const p = new Intl.NumberFormat('nl-NL', {
                        style: 'currency',
                        currency: 'EUR',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(Number(value))
                    return p.replace(/,00(?=\D*$)/, ',-')
                }

                this.store.vehicleList.forEach((vehicle: any) => {
                    const onRequest = this.store.t('On request')
                    const purchase = vehicle.prices?.purchase
                    const purchaseValue = purchase?.value != null && Number(purchase.value) !== 0 ? formatPurchasePrice(purchase.value) : onRequest
                    const prev = purchase?.previousValue
                    const hasDiscount = prev != null && prev !== '' && String(prev) !== '0.0' && Number.isFinite(Number(prev)) && Number(prev) !== 0
                    const oldPriceHtml = hasDiscount ? `<s class="stock-block__card-oldprice">${formatPurchasePrice(prev)}</s>` : ''
                    const privateLeaseValue = vehicle.prices?.runningCalculatorPrivateLease
                        ? `${formatPurchasePrice(vehicle.prices.runningCalculatorPrivateLease.value)} p.m.*`
                        : onRequest
                    const financePrice = Number(vehicle.prices?.purchase?.value) || 0
                    let businessLeaseValue = onRequest
                    let financeValue = onRequest
                    if (financePrice && this.interestRate && this.defaultTerms) {
                        const downPayment = (financePrice / 100) * this.defaultDownPaymentPercentage
                        const finalTerm = (financePrice / 100) * this.defaultFinalTermPercentage
                        const principal = financePrice - downPayment

                        const financeRate = this.interestRate / 12 / 100
                        const financeRatePow = Math.pow(1 + financeRate, this.defaultTerms)
                        const financeMonthly = ((principal - finalTerm / financeRatePow) * (financeRate * financeRatePow)) / (financeRatePow - 1)
                        if (financeMonthly > 0) {
                            financeValue = `${formatPurchasePrice(Math.round(financeMonthly))} p.m.`
                        }

                        const leaseRate = Math.pow(this.interestRate / 100 + 1, 1 / 12) - 1
                        const leaseRatePow = Math.pow(1 + leaseRate, this.defaultTerms)
                        const leaseMonthly = ((principal - finalTerm / leaseRatePow) * (leaseRate * leaseRatePow)) / (leaseRatePow - 1)
                        if (leaseMonthly > 0) {
                            businessLeaseValue = `${formatPurchasePrice(Math.round(leaseMonthly))} p.m.`
                        }
                    }

                    newCardsHTML += `<li class="splide__slide" :data-loading="$store.filterStore.isLoading">
                                        <a href="/voorraad/${vehicle.slug}" class="slider__card-link">
                                            <div class="slider__card">
                                                <div class="slider__card-image">
                                                    <picture class="model-block__picture">
                                                        <img class="model-block__image" src="${vehicle.media[0].url}" loading="lazy" alt="${vehicle.brand}" width="420" height="250">
                                                    </picture>
                                                    <div class="badge badge--location">${vehicle.establishment}</div>
                                                </div>
                                                <div class="slider__card-content">
                                                    <strong class="h3">${vehicle.brand} ${vehicle.model}</strong>
                                                    <p class="slider__card-subtitle">${vehicle.edition} ${vehicle.attributes.addition ? vehicle.attributes.addition : ''}</p>

                                                    <div class="stock-block__card-details">
                                                        <div class="stock-block__card-column">
                                                            <div class="stock-block__card-row">
                                                                <i class="fa-regular fa-calendar-days"></i> <span>${vehicle.attributes.yearOfConstruction}</span>
                                                            </div>
                                                            <div class="stock-block__card-row">
                                                                <i class="fa-regular fa-gears"></i> <span>${this.store.t(vehicle.attributes.transmission)}</span>
                                                            </div>
                                                        </div>
                                                        <div class="stock-block__card-column">
                                                            <div class="stock-block__card-row">
                                                                <i class="fa-regular fa-gauge-low"></i> <span>${vehicle.attributes.odometerReading}</span>
                                                            </div>
                                                            <div class="stock-block__card-row">
                                                                <i class="fa-regular fa-gas-pump"></i> <span>${this.store.t(vehicle.attributes.fuel)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="stock-block__card-price">
                                                        <div class="stock-block__card-priceline">
                                                            <span class="stock-block__card-pricelabel">${this.store.t('Drive away for')}</span>
                                                            <span class="stock-block__card-pricevalue stock-block__card-newprice">${oldPriceHtml}${purchaseValue}</span>
                                                        </div>
                                                        <div class="stock-block__card-priceline">
                                                            <span class="stock-block__card-pricelabel">${this.store.t('Private lease from ')}</span>
                                                            <span class="stock-block__card-pricevalue">${privateLeaseValue}</span>
                                                        </div>
                                                        <div class="stock-block__card-priceline">
                                                            <span class="stock-block__card-pricelabel">${this.store.t('Business lease from ')}</span>
                                                            <span class="stock-block__card-pricevalue">${businessLeaseValue}</span>
                                                        </div>
                                                        <div class="stock-block__card-priceline">
                                                            <span class="stock-block__card-pricelabel">${this.store.t('Finance from')}</span>
                                                            <span class="stock-block__card-pricevalue">${financeValue}</span>
                                                        </div>
                                                    </div>
                                                    <button class="slider__card-button btn btn--ghost">
                                                        <span>Bekijk deze auto</span>
                                                        <i class="fa-light fa-arrow-right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </a>
                                    </li>`
                })

                newCardsHTML += '</div>'

                Alpine.morph(cardsContainer, newCardsHTML)
            }
        },

        /**
         * Looks in the vehicle list to find the minimum value available for key
         *
         * @param filterKey
         * @returns minimum value
         */
        getMinimumValue(filterKey: string, options?: Array<{ key: any }>): number {
            if (!options || options.length === 0) {
                return 0
            }

            const numericValues = options.map((o) => Number(o.key)).filter((v) => Number.isFinite(v))

            if (numericValues.length === 0) {
                return 0
            }

            return Math.min(...numericValues)
        },

        /**
         * Looks in the vehicle list to find the maximum value available for key
         *
         * @param filterKey
         * @returns maximum value
         */
        getMaximumValue(filterKey: string, options?: Array<{ key: any }>): number {
            if (!options || options.length === 0) {
                if (filterKey === 'price') {
                    return 150000
                } else if (filterKey === 'odometerReading') {
                    return 140000
                } else if (filterKey === 'actionRadius') {
                    return 1000
                } else if (filterKey === 'batteryCapacity') {
                    return 150
                } else if (filterKey === 'batteryCondition') {
                    return 100
                }

                return 150000
            }

            const numericValues = options.map((o) => Number(o.key)).filter((v) => Number.isFinite(v))

            if (numericValues.length === 0) {
                return 0
            }

            return Math.max(...numericValues)
        },

        // Add this method to your Alpine component
        filterOptions(filter: any) {
            // container that holds the checkbox rows
            const container = this.$el.closest('.vlp__filters-values-container')
            if (!container) return

            const q = String(filter?.searchTerm ?? '')
                .trim()
                .toLowerCase()

            // Each row is one option
            const rows = container.querySelectorAll('.vlp__filters-values--checkboxes')

            rows.forEach((row) => {
                // Label span is the first <span> (per your snippet)
                const labelEl = row.querySelector('span')
                const label = (labelEl?.textContent || '').toLowerCase()

                // When query is empty, show all; else show matches only
                const isMatch = !q || label.includes(q)

                // Toggle 'hidden' class (items that don't match get 'hidden')
                row.classList.toggle('hidden', !isMatch)
            })
        },

        /**
         * Updates the sort order based on the select dropdown
         */
        updateSort() {
            // Get the selected sort value
            const val = (this.$el as HTMLSelectElement)?.value

            if (val && val !== this.store.sort) {
                this.store.setSort(val) // write to store; watcher triggers apply
            }
        },

        /**
         * Navigates to a specific page in the vehicle list.
         *
         * @param pageNumber The page number to navigate to.
         */
        goToPage(pageNumber: number) {
            const pageCount = this.store.pageCount || 0
            if (pageNumber > 0 && pageNumber <= pageCount) {
                this.store.pageCurrent = pageNumber
                this.applyFilters(true)
            }
        },
    }))
}
